import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'localization';
import { ChannelItem, ChannelItemType } from 'types/channel';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import './DraggableItemListMolecule.scss';

const ITEM_TYPE_ICONS: Record<ChannelItemType, string> = {
  approach: '\uD83D\uDCCB',
  surface_template: '\uD83D\uDDBC\uFE0F',
  assistant: '\uD83E\uDD16',
  video: '\uD83C\uDFAC',
  form: '\uD83D\uDCDD',
  metaspace: '\uD83D\uDDC2\uFE0F',
  schema: '\uD83D\uDCD0', // triangular ruler \u2014 structure/definition
  context: '\uD83D\uDD0D', // magnifying glass \u2014 lens/view
  arc_template: '\uD83C\uDF00', // cyclone \u2014 staged flow
  orbit_template: '\uD83E\uDE90', // ringed planet \u2014 orbit/cadence
  aspect: '\uD83C\uDFF7\uFE0F', // label tag \u2014 named unit of shape
  aspect_group: '\uD83C\uDFF7\uFE0F', // EAINT-864 Phase 1 registration; Phase 3 gives this its own glyph
  composition: '\uD83E\uDDE9', // puzzle piece \u2014 reusable composed subtree
  archetype: '\uD83C\uDFF7\uFE0F', // label tag \u2014 classification vocabulary
};

const ITEM_TYPE_KEYS: Record<ChannelItemType, string> = {
  approach: 'marketplace.common.itemType.approach',
  surface_template: 'marketplace.common.itemType.surfaceTemplate',
  assistant: 'marketplace.common.itemType.assistant',
  video: 'marketplace.common.itemType.video',
  form: 'marketplace.common.itemType.form',
  metaspace: 'marketplace.common.itemType.metaspace',
  schema: 'marketplace.common.itemType.schema',
  context: 'marketplace.common.itemType.context',
  arc_template: 'marketplace.common.itemType.arcTemplate',
  orbit_template: 'marketplace.common.itemType.orbitTemplate',
  aspect: 'marketplace.common.itemType.aspect',
  aspect_group: 'marketplace.common.itemType.aspectGroup',
  composition: 'marketplace.common.itemType.composition',
  archetype: 'marketplace.common.itemType.archetype',
};

export interface DraggableItemListMoleculeProps {
  items: ChannelItem[];
  onReorder: (itemIds: string[]) => void;
  onRemove: (item: ChannelItem) => void;
  onItemClick?: (item: ChannelItem, rowElement: HTMLElement) => void;
  onItemEdit?: (item: ChannelItem) => void;
  disabled?: boolean;
  className?: string;
}

const DraggableItemListMolecule: React.FC<DraggableItemListMoleculeProps> = ({
  items,
  onReorder,
  onRemove,
  onItemClick,
  onItemEdit,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const dragCounter = useRef<Record<number, number>>({});

  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      if (disabled) return;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
      setDragIndex(index);
    },
    [disabled]
  );

  const handleDragEnter = useCallback(
    (index: number) => {
      if (disabled || dragIndex === null) return;
      if (!dragCounter.current[index]) {
        dragCounter.current[index] = 0;
      }
      dragCounter.current[index]++;
      setOverIndex(index);
    },
    [disabled, dragIndex]
  );

  const handleDragLeave = useCallback(
    (index: number) => {
      if (!dragCounter.current[index]) return;
      dragCounter.current[index]--;
      if (dragCounter.current[index] <= 0) {
        dragCounter.current[index] = 0;
        if (overIndex === index) {
          setOverIndex(null);
        }
      }
    },
    [overIndex]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    },
    [disabled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, toIndex: number) => {
      e.preventDefault();
      dragCounter.current = {};

      if (disabled || dragIndex === null || dragIndex === toIndex) {
        setDragIndex(null);
        setOverIndex(null);
        return;
      }

      const reordered = [...items];
      const [moved] = reordered.splice(dragIndex, 1);
      reordered.splice(toIndex, 0, moved);

      setDragIndex(null);
      setOverIndex(null);
      onReorder(reordered.map((item) => item.id));
    },
    [disabled, dragIndex, items, onReorder]
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setOverIndex(null);
    dragCounter.current = {};
  }, []);

  const classes = ['marketplace-draggable-item-list', className].filter(Boolean).join(' ');

  return (
    <div className={classes} role="list">
      {items.map((item, index) => {
        const displayName =
          getLocalizedText(item.item_metadata?.name) || t(ITEM_TYPE_KEYS[item.item_type]);
        const isDragging = dragIndex === index;
        const isOver = overIndex === index && dragIndex !== index;

        const isClickable = Boolean(onItemClick);

        const rowClasses = [
          'marketplace-draggable-item-list__row',
          isDragging ? 'marketplace-draggable-item-list__row--dragging' : '',
          isOver ? 'marketplace-draggable-item-list__row--over' : '',
          disabled ? 'marketplace-draggable-item-list__row--disabled' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div
            key={item.id}
            className={rowClasses}
            role="listitem"
            draggable={!disabled}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragEnter={() => handleDragEnter(index)}
            onDragLeave={() => handleDragLeave(index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            {/* Drag handle */}
            <span
              className="marketplace-draggable-item-list__handle"
              title={t('marketplace.channelManage.content.dragHandle')}
              aria-hidden="true"
            >
              <i className="pi pi-bars" />
            </span>

            {/* Type icon */}
            <span
              className="marketplace-draggable-item-list__icon"
              title={t(ITEM_TYPE_KEYS[item.item_type])}
              aria-hidden="true"
            >
              {ITEM_TYPE_ICONS[item.item_type]}
            </span>

            {/* Name + meta */}
            <div className="marketplace-draggable-item-list__info">
              <span className="marketplace-draggable-item-list__name">{displayName}</span>
              <span className="marketplace-draggable-item-list__meta">
                <span className="marketplace-draggable-item-list__type-badge">
                  {t(ITEM_TYPE_KEYS[item.item_type])}
                </span>
                {item.item_metadata?.system_name && (
                  <>
                    <span className="marketplace-draggable-item-list__separator" aria-hidden="true">&middot;</span>
                    <span className="marketplace-draggable-item-list__version">
                      {item.item_metadata.system_name}
                    </span>
                  </>
                )}
                {Array.isArray((item.item_metadata as Record<string, unknown>)?.['tags']) &&
                  ((item.item_metadata as Record<string, unknown>)?.['tags'] as string[]).length > 0 && (
                  <>
                    <span className="marketplace-draggable-item-list__separator" aria-hidden="true">&middot;</span>
                    <span className="marketplace-draggable-item-list__tags">
                      {((item.item_metadata as Record<string, unknown>)?.['tags'] as string[]).join(', ')}
                    </span>
                  </>
                )}
              </span>
            </div>

            {/* Status badge */}
            <span
              className={[
                'marketplace-draggable-item-list__status',
                item.item_status === 'draft'
                  ? 'marketplace-draggable-item-list__status--draft'
                  : 'marketplace-draggable-item-list__status--live',
              ].join(' ')}
            >
              {item.item_status === 'draft'
                ? t('marketplace.content.draftBadge', 'DRAFT')
                : t('marketplace.channelManage.content.liveBadge', 'LIVE')}
            </span>

            {/* Action buttons */}
            <div className="marketplace-draggable-item-list__actions">
              {onItemEdit && (
                <button
                  type="button"
                  className="marketplace-draggable-item-list__action-btn"
                  onClick={(e) => { e.stopPropagation(); onItemEdit(item); }}
                  disabled={disabled}
                  title={t('marketplace.channelManage.content.editItem', 'Edit')}
                  aria-label={t('marketplace.channelManage.content.editItem', 'Edit')}
                >
                  <i className="pi pi-pencil" />
                </button>
              )}
              <button
                type="button"
                className="marketplace-draggable-item-list__action-btn marketplace-draggable-item-list__action-btn--danger"
                onClick={(e) => { e.stopPropagation(); onRemove(item); }}
                disabled={disabled}
                title={t('marketplace.channelManage.content.removeItem')}
                aria-label={t('marketplace.channelManage.content.removeItem')}
              >
                <i className="pi pi-trash" />
              </button>
              {isClickable && (
                <button
                  type="button"
                  className="marketplace-draggable-item-list__open-btn"
                  onClick={(e) => { e.stopPropagation(); onItemClick?.(item, e.currentTarget); }}
                  title={t('marketplace.channelManage.content.openItem', 'Open')}
                >
                  {t('marketplace.channelManage.content.openItem', 'Open')}
                  <i className="pi pi-chevron-right" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DraggableItemListMolecule;
